import {
  Blend,
  ChevronDown,
  ChevronRight,
  Circle,
  Code2,
  Component,
  Copy,
  Diff,
  FlipHorizontal2,
  Frame,
  Group,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Minus,
  Pentagon,
  Repeat2,
  Scan,
  Square,
  SquaresIntersect,
  SquaresUnite,
  Star,
  Type,
} from "lucide-react";
import { memo, useState, type DragEvent, type ReactNode } from "react";
import { getSceneNodeChildren } from "@/document/model/document";
import { frameHasScript } from "@/document/model/frameScript";
import { documentStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { frameAutoLayoutMode, type BooleanOperation, type SceneNode } from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import type { LayerDropTarget, MaskDecoration } from "./layerList";
import { layerDisplayName } from "./layerList";

type DropPlacement = "before" | "inside" | "after";
type ParentType = LayerDropTarget["parentType"];

export interface FlatLayerRowDropIndicator {
  nodeId: string;
  placement: DropPlacement;
}

export interface SidebarSelectionModifiers {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}

export interface FlatLayerNodeRowProps {
  kind: "node";
  node: SceneNode;
  depth: number;
  index: number;
  maskDecoration?: MaskDecoration;
  parentId: string;
  parentType: ParentType;
  selected: boolean;
  primary: boolean;
  canvasHovered: boolean;
  collapsed: boolean;
  dragging: boolean;
  dropPlacement?: DropPlacement;
  editing: boolean;
  onSelectNode: (nodeId: string, modifiers: SidebarSelectionModifiers) => void;
  onRenameNode: (nodeId: string, name: string) => void;
  onVisibilityChange: (nodeId: string, visible: boolean) => void;
  onLockedChange: (nodeId: string, locked: boolean) => void;
  onContextMenu: (nodeId: string, point: { x: number; y: number }) => void;
  onToggleCollapsed: (nodeId: string) => void;
  onDragStartNode: (nodeId: string, event: DragEvent<HTMLDivElement>) => void;
  onDragEndNode: () => void;
  onDragOverNode: (indicator: FlatLayerRowDropIndicator | undefined) => void;
  onDropNode: (target: LayerDropTarget) => void;
  isValidDropTarget: (target: LayerDropTarget) => boolean;
  onEditNode: (nodeId: string | undefined) => void;
  onRowElement: (nodeId: string, element: HTMLDivElement | null) => void;
  draggedNodeIds: string[];
  allowScripts?: boolean;
  onEditScript?: (nodeId: string) => void;
}

export interface FlatLayerEmptyDropRowProps {
  kind: "empty-drop";
  depth: number;
  parentId: string;
  parentType: ParentType;
  onMoveNodes: (nodeIds: string[], target: LayerDropTarget) => void;
  onDragEndNode: () => void;
  isValidDropTarget: (target: LayerDropTarget) => boolean;
  draggedNodeIds: string[];
}

export type FlatLayerRowComponentProps = FlatLayerNodeRowProps | FlatLayerEmptyDropRowProps;

export const FlatLayerRow = memo(function FlatLayerRow(props: FlatLayerRowComponentProps) {
  if (props.kind === "empty-drop") {
    return <EmptyContainerDropRow {...props} />;
  }
  return <LayerNodeRow {...props} />;
});

function LayerNodeRow({
  node,
  depth,
  index,
  maskDecoration,
  parentId,
  parentType,
  selected,
  primary,
  canvasHovered,
  collapsed,
  dragging,
  dropPlacement,
  editing,
  onSelectNode,
  onRenameNode,
  onVisibilityChange,
  onLockedChange,
  onContextMenu,
  onToggleCollapsed,
  onDragStartNode,
  onDragEndNode,
  onDragOverNode,
  onDropNode,
  isValidDropTarget,
  onEditNode,
  onRowElement,
  draggedNodeIds,
  allowScripts = false,
  onEditScript,
}: FlatLayerNodeRowProps) {
  const document = useStoreSubscribe(documentStore.document);
  const hasAttachedScript = allowScripts && frameHasScript(document, node);
  const children = getSceneNodeChildren(node);
  const isContainer = Boolean(children);
  const Icon = layerIcon(node);
  const layerName = layerDisplayName(node);
  const componentLayer = node.type === "component" || node.type === "componentVariant" || node.type === "instance";
  const containerLayer = node.type === "frame" || node.type === "group" || node.type === "boolean" || componentLayer;
  const maskRailLeft = 7 + (maskDecoration?.railDepth ?? depth) * 14;

  function dropTargetForPlacement(placement: DropPlacement): LayerDropTarget {
    if (placement === "inside" && children) {
      return { parentId: node.id, parentType: node.type as ParentType, index: children.length };
    }

    return {
      parentId,
      parentType,
      index: index + (placement === "before" ? 1 : 0),
    };
  }

  return (
    <div
      ref={element => onRowElement(node.id, element)}
      role="button"
      tabIndex={0}
      draggable={!editing}
      onClick={event => onSelectNode(node.id, event)}
      onKeyDown={event => {
        if (editing) {
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelectNode(node.id, event);
        }
      }}
      onDoubleClick={event => {
        event.preventDefault();
        onEditNode(node.id);
      }}
      onContextMenu={event => {
        event.preventDefault();
        onSelectNode(node.id, event);
        onContextMenu(node.id, { x: event.clientX, y: event.clientY });
      }}
      onDragStart={event => onDragStartNode(node.id, event)}
      onDragEnd={onDragEndNode}
      onDragOver={event => {
        if (draggedNodeIds.length === 0 || draggedNodeIds.includes(node.id)) {
          return;
        }
        const target = dropTargetForPlacement(dropPlacementForEvent(event, Boolean(children)));
        if (!isValidDropTarget(target)) {
          onDragOverNode(undefined);
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        onDragOverNode({ nodeId: node.id, placement: dropPlacementForEvent(event, Boolean(children)) });
      }}
      onDrop={event => {
        if (!dropPlacement || draggedNodeIds.length === 0) {
          return;
        }
        const target = dropTargetForPlacement(dropPlacement);
        if (!isValidDropTarget(target)) {
          return;
        }
        event.preventDefault();
        onDropNode(target);
      }}
      className={`group relative flex h-8 w-full items-center gap-1 border-l px-2 text-left outline-none ${
        selected
          ? primary
            ? "border-sky-300 bg-sky-500/15 text-sky-50"
            : "border-sky-500 bg-sky-500/10 text-sky-100"
          : canvasHovered ? "border-white/10 bg-white/[0.07] text-slate-300 hover:bg-white/[0.07]" : "border-white/10 text-slate-400 hover:bg-white/[0.04]"
      } ${dropPlacement === "inside" ? "bg-sky-400/10 ring-1 ring-inset ring-sky-300/50" : ""} ${dragging ? "opacity-45" : ""}`}
      style={{ paddingLeft: 12 + depth * 14 }}
    >
      {maskDecoration?.maskedBySibling ? (
        <span
          className="pointer-events-none absolute bottom-0 top-0 w-px bg-slate-500/70"
          style={{ left: maskRailLeft }}
          aria-hidden="true"
        />
      ) : null}
      {maskDecoration?.maskAffectsSiblings ? (
        <>
          <span
            className="pointer-events-none absolute top-0 h-1/2 w-px bg-slate-500/70"
            style={{ left: maskRailLeft }}
            aria-hidden="true"
          />
          <span
            className="pointer-events-none absolute top-1/2 h-px w-2 bg-slate-500/70"
            style={{ left: maskRailLeft }}
            aria-hidden="true"
          />
        </>
      ) : null}
      {dropPlacement === "before" ? <DropLine position="top" /> : null}
      {dropPlacement === "after" ? <DropLine position="bottom" /> : null}
      {isContainer ? (
        <button
          type="button"
          className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-sm text-slate-500 hover:bg-white/10 hover:text-slate-200"
          aria-label={collapsed ? `Expand ${layerName}` : `Collapse ${layerName}`}
          onClick={event => {
            event.stopPropagation();
            onToggleCollapsed(node.id);
          }}
          onDoubleClick={event => event.stopPropagation()}
          draggable={false}
        >
          {collapsed ? <ChevronRight className="h-3 w-3" aria-hidden="true" /> : <ChevronDown className="h-3 w-3" aria-hidden="true" />}
        </button>
      ) : (
        <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      <Icon className={`h-3.5 w-3.5 shrink-0 ${node.isMask ? "text-slate-300" : componentLayer ? selected ? "text-violet-200" : "text-violet-300" : ""}`} aria-hidden="true" />
      {editing ? (
        <RenameInput
          name={layerName}
          onCommit={name => {
            onEditNode(undefined);
            onRenameNode(node.id, name);
          }}
          onCancel={() => onEditNode(undefined)}
        />
      ) : (
        <span className={`min-w-0 flex-1 truncate ${containerLayer ? "font-medium text-slate-200" : ""} ${componentLayer ? selected ? "text-violet-100" : "text-violet-200" : ""}`}>
          {layerName}
        </span>
      )}
      {!editing ? (
        <div className={`ml-auto flex shrink-0 items-center justify-end gap-0.5 ${hasAttachedScript ? "w-[3.75rem]" : "w-11"}`}>
          {hasAttachedScript && onEditScript ? (
            <LayerStateButton
              label={`Edit script for ${layerName}`}
              persistent
              active
              onClick={() => onEditScript(node.id)}
            >
              <Code2 className="size-3.5" aria-hidden="true" />
            </LayerStateButton>
          ) : null}
          <LayerStateButton
            label={node.visible ? `Hide ${layerName}` : `Show ${layerName}`}
            persistent={!node.visible}
            active={node.visible}
            onClick={() => onVisibilityChange(node.id, !node.visible)}
          >
            {node.visible ? <Eye className="size-3.5" aria-hidden="true" /> : <EyeOff className="size-3.5" aria-hidden="true" />}
          </LayerStateButton>
          <LayerStateButton
            label={node.locked ? `Unlock ${layerName}` : `Lock ${layerName}`}
            persistent={node.locked}
            active={!node.locked}
            onClick={() => onLockedChange(node.id, !node.locked)}
          >
            {node.locked ? <Lock className="size-3.5" aria-hidden="true" /> : <Unlock className="size-3.5" aria-hidden="true" />}
          </LayerStateButton>
        </div>
      ) : null}
    </div>
  );
}

function EmptyContainerDropRow({
  depth,
  parentId,
  parentType,
  onMoveNodes,
  onDragEndNode,
  isValidDropTarget,
  draggedNodeIds,
}: FlatLayerEmptyDropRowProps) {
  return (
    <div
      className="h-5 border-l border-white/10"
      style={{ marginLeft: 12 + depth * 14 }}
      onDragOver={event => {
        if (draggedNodeIds.length === 0 || draggedNodeIds.includes(parentId)) {
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }}
      onDrop={event => {
        event.preventDefault();
        const target = { parentId, parentType, index: 0 };
        if (draggedNodeIds.length > 0 && isValidDropTarget(target)) {
          onMoveNodes(draggedNodeIds, target);
        }
        onDragEndNode();
      }}
    />
  );
}

function RenameInput({ name, onCommit, onCancel }: { name: string; onCommit: (name: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(name);
  const trimmedDraft = draft.trim();
  return (
    <input
      className="min-w-0 flex-1 rounded-sm border border-sky-400/70 bg-[#0f1318] px-1 py-0 text-[12px] text-slate-50 outline-none"
      value={draft}
      autoFocus
      onFocus={event => event.currentTarget.select()}
      onChange={event => setDraft(event.target.value)}
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onBlur={() => onCommit(trimmedDraft || name)}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit(trimmedDraft || name);
        } else if (event.key === "Escape") {
          event.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

function LayerStateButton({
  label,
  persistent,
  active,
  onClick,
  children,
}: {
  label: string;
  persistent: boolean;
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      draggable={false}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
      onDoubleClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onPointerDown={event => event.stopPropagation()}
      className={`grid size-5 place-items-center rounded-sm ${
        active ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-200"
      } hover:bg-white/10 ${persistent ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"}`}
    >
      {children}
    </button>
  );
}

function DropLine({ position }: { position: "top" | "bottom" }) {
  return (
    <span
      className={`pointer-events-none absolute left-2 right-2 z-10 h-px bg-sky-300 shadow-[0_0_0_1px_rgba(125,211,252,0.35)] ${
        position === "top" ? "top-0" : "bottom-0"
      }`}
      aria-hidden="true"
    />
  );
}

function dropPlacementForEvent(event: DragEvent<HTMLElement>, canDropInside: boolean): DropPlacement {
  const rect = event.currentTarget.getBoundingClientRect();
  const localY = event.clientY - rect.top;
  if (!canDropInside) {
    return localY < rect.height / 2 ? "before" : "after";
  }

  if (localY < rect.height * 0.28) {
    return "before";
  }
  if (localY > rect.height * 0.72) {
    return "after";
  }
  return "inside";
}

function layerIcon(node: SceneNode): typeof Square {
  if (node.isMask) {
    return Scan;
  }

  if (node.type === "boolean") {
    return booleanIcon(node.operation);
  }

  if (node.type === "primitive") {
    return primitiveIcon(node.primitive.kind);
  }

  switch (node.type) {
    case "frame":
      return frameLayerIcon(node);
    case "group":
      return Group;
    case "component":
      return Component;
    case "componentVariant":
      return Component;
    case "instance":
      return Copy;
    case "text":
      return Type;
    case "vectorNetwork":
    case "shape":
      return Pentagon;
    default:
      return Square;
  }
}

function frameLayerIcon(node: Extract<SceneNode, { type: "frame" }>): typeof Square {
  const mode = frameAutoLayoutMode(node);
  if (mode === "repeat") {
    return Repeat2;
  }
  if (mode === "mirror") {
    return FlipHorizontal2;
  }
  return Frame;
}

function booleanIcon(operation: BooleanOperation): typeof Square {
  switch (operation) {
    case "union":
      return SquaresUnite;
    case "subtract":
      return Diff;
    case "intersect":
      return SquaresIntersect;
    case "exclude":
      return Blend;
  }
}

function primitiveIcon(kind: PrimitiveKind): typeof Square {
  switch (kind) {
    case "oval":
      return Circle;
    case "line":
      return Minus;
    case "polygon":
      return Pentagon;
    case "star":
      return Star;
    case "frame":
      return Frame;
    case "rectangle":
      return Square;
  }
}
